import PropTypes from 'prop-types'
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Box,
  SvgIcon,
  Stack,
  Chip,
  useMediaQuery,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import DotDivider from '@mui/icons-material/FiberManualRecord'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { useState } from 'react'

import AngleDown from '@hapana/shared/web/styles/assets/svgs/angle-down-small.svg'
import Button from '../button/button.component'
import { UpgradeTooltip } from '../feature-gate'

import { BUTTON_VARIANTS } from '../button/button.constants'

import { styles } from './accordion.styles'

const ExpandIcon = (buttonProps) => (
  <Box {...buttonProps} sx={styles.iconButton}>
    <SvgIcon sx={styles.expandIcon} component={AngleDown} />
  </Box>
)

const MORE_VERTICAL_ICON = 'MORE_VERTICAL_ICON'

const Accordion = ({
  label,
  id,
  content,
  groupedRows,
  button,
  secondaryButton,
  description,
  chip,
  containerStyles,
  contentStyles,
  summaryStyles,
  expandLabel,
  onChange,
  isOpen,
  expanded: controlledExpanded,
  hasColumnLayout,
  labelStyles,
  customLabel,
  moreVertIcon,
  dragIndicator,
  borderBottomSummary,
  isViewOnly,
  summaryWrapperStyles,
  lockedCheckResult,
}) => {
  const stopPropagation = (event) => {
    event.stopPropagation()
    button.onClick?.(event)
  }

  const matchesDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'))

  const handleActionClicked = (event) => {
    event.stopPropagation()
  }

  const [internalExpanded, setInternalExpanded] = useState(!!isOpen)

  // OC-320 plan-gated section: greyed header + a lock whose hover explains the gate; the section is
  // non-expandable (no wall). Additive — inert unless the caller passes an un-entitled checkResult.
  const locked = Boolean(lockedCheckResult) && lockedCheckResult.entitled === false

  const isControlled = controlledExpanded !== undefined
  const isExpanded = locked ? false : isControlled ? controlledExpanded : internalExpanded

  const handleChange = () => {
    if (isViewOnly || locked) {
      return
    }
    if (!isControlled) {
      setInternalExpanded(!isExpanded)
    }
    onChange?.(!isExpanded)
  }

  return (
    <MuiAccordion
      sx={{
        ...styles.accordion,
        ...(groupedRows && styles.accordionGhost),
        ...containerStyles,
      }}
      disableGutters
      expanded={isExpanded}
      onChange={handleChange}
    >
      <Box
        sx={{
          ...styles.summaryWrapper,
          ...(button && styles.summaryWrapperButton),
          ...(groupedRows && styles.summaryWrapperGhost),
          ...summaryWrapperStyles,
        }}
      >
        <AccordionSummary
          sx={{
            ...styles.summary,
            ...(groupedRows && styles.summaryGhost),
            ...(expandLabel && matchesDesktop
              ? styles.expandLabelSummary
              : styles.expandLabelSummaryMobile),
            ...summaryStyles,
            ...(isViewOnly && styles.viewOnly),
          }}
          expandIcon={
            isViewOnly ? null : locked ? (
              <UpgradeTooltip checkResult={lockedCheckResult} placement="left">
                <LockOutlinedIcon sx={styles.lockIcon} />
              </UpgradeTooltip>
            ) : (
              <ExpandIcon />
            )
          }
          aria-controls={`${id}-${'content'}`}
          id={`${id}-header`}
          data-testid={id !== undefined ? `accordion-summary-${id}` : undefined}
        >
          {dragIndicator && <DragIndicatorIcon />}
          {customLabel || (
            <>
              <Stack
                sx={hasColumnLayout ? styles.columnStartContentSummary : styles.startContentSummary}
                spacing={1}
                direction={hasColumnLayout ? 'column' : 'row'}
                divider={hasColumnLayout ? null : <DotDivider />}
                width="100%"
              >
                <Typography
                  sx={[styles.label, labelStyles, locked && styles.lockedLabel]}
                  component="span"
                >
                  {label}
                </Typography>
                {(description || chip) && (
                  <Stack sx={styles.descriptionStack} direction="row" spacing={2}>
                    {description && <Typography sx={styles.description}>{description}</Typography>}
                    {chip && (
                      <Chip
                        color="primary"
                        sx={styles.chip}
                        size="small"
                        label={chip.label}
                        icon={chip.icon && <SvgIcon component={chip.icon} />}
                      />
                    )}
                  </Stack>
                )}
              </Stack>
            </>
          )}
          {moreVertIcon && (
            <Button
              type={'button'}
              icon={MoreVertIcon}
              variant={BUTTON_VARIANTS.ICON}
              name={MORE_VERTICAL_ICON}
              onClick={handleActionClicked}
            />
          )}
          {secondaryButton && (
            <Button
              variant={BUTTON_VARIANTS.TEXT}
              type="button"
              {...secondaryButton}
              sx={[styles.button, secondaryButton.sx]}
              onClick={(event) => {
                event.stopPropagation()
                secondaryButton.onClick?.(event)
              }}
            />
          )}
          {button && (
            <Button
              variant={BUTTON_VARIANTS.TEXT}
              sx={styles.button}
              type="button"
              data-testid={button.testId}
              {...button}
              onClick={stopPropagation}
            />
          )}
          {expandLabel && (
            <Box sx={styles.expandLabelWrapper}>
              <Typography typography="header4" color="gray.80">
                {expandLabel}
              </Typography>
            </Box>
          )}
        </AccordionSummary>
      </Box>
      <AccordionDetails
        sx={{
          ...(groupedRows && styles.detailsGhost),
          ...contentStyles,
          ...(borderBottomSummary && isExpanded && styles.borderTopSummary),
        }}
      >
        {content}
      </AccordionDetails>
    </MuiAccordion>
  )
}

Accordion.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  content: PropTypes.node.isRequired,
  groupedRows: PropTypes.bool,
  button: PropTypes.shape({
    label: PropTypes.string.isRequired,
    startIcon: PropTypes.elementType,
    onClick: PropTypes.func,
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    testId: PropTypes.string,
  }),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chip: PropTypes.shape({
    label: PropTypes.string,
    icon: PropTypes.elementType,
  }),
  containerStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  contentStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  summaryStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  expandLabel: PropTypes.string,
  onChange: PropTypes.func,
  isOpen: PropTypes.bool,
  expanded: PropTypes.bool,
  hasColumnLayout: PropTypes.bool,
  labelStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  customLabel: PropTypes.node,
  moreVertIcon: PropTypes.node,
  dragIndicator: PropTypes.node,
  borderBottomSummary: PropTypes.bool,
  isViewOnly: PropTypes.bool,
  summaryWrapperStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  // OC-320: when the section is plan-gated, pass the feature's checkResult — the header greys, a lock
  // replaces the chevron, its hover explains the gate, and the section no longer expands.
  lockedCheckResult: PropTypes.object,
}
Accordion.defaultProps = {
  groupedRows: false,
  button: null,
  isViewOnly: false,
}

export default Accordion
